import * as React from 'react';
import { Secondary } from 'rcd-cpp-ui/ui/button';
import { sleep } from 'rcd-cpp-workspace/utils/sleep';
import { useSelector } from './../../utils/useSelector';
import { Overlay, Spinner } from 'rcd-cpp-ui/ui/loader';
import { Typeahead } from '../Typeahead/typeahead';
import { useDispatch } from 'react-redux';
import { Role } from '../../enums/Role';

declare global {
  interface Window {
    regressionInfo: {
      completedWorkspaceRefIds: string[];
      completedViewRefIds: string[];
    };
  }
}

export const RegressionSuite = () => {
  if (process.env.ENV == 'prod') return null;
  const isDeveloper = useSelector(s =>
    s.app?.user?.roles?.includes(Role.developer)
  );
  const isRegressionMode = useSelector(s => s.planning?.regressionMode);
  if (!isDeveloper) return null;
  const pathname = window.location.pathname;
  const dispatch = useDispatch();
  React.useEffect(() => {
    if (!isRegressionMode && pathname == '/w/regression') {
      dispatch({ type: 'UPDATE_REGRESSION_MODE' });
    }
  }, [pathname]);
  if (!isRegressionMode) return null;
  return <RegressionOverlay />;
};

const RegressionOverlay = () => {
  const workspaces = useSelector(s => s.planning?.workspaces);
  const [isLoading, setLoading] = React.useState(false);
  const [msg, setMsg] = React.useState('Run Regression Test');
  const [workspace, setWorkspace] = React.useState(null);
  if (!workspaces?.length) return null;
  const options = workspaces.map(w => w.props);
  return (
    <Overlay opaque id="regressionOverlay">
      <div style={{ padding: '20px', backgroundColor: 'white' }}>
        {!isLoading && (
          <Typeahead
            value={workspace?.name || ''}
            options={options}
            placeholder="Select a Workspace to begin regression from"
            onSelect={w => setWorkspace(w)}
          />
        )}
        <Secondary
          disabled={isLoading}
          onClick={() => {
            setLoading(true);
            runRegressionTest(workspace?.refId)
              .then(() => {
                setMsg('Completed');
              })
              .catch(e => {
                setMsg('Error: ' + e.message);
              });
          }}
          style={{ marginTop: '10px', width: '100%' }}
        >
          {isLoading && msg != 'Completed' && <Spinner spinnerSize="icon" />}{' '}
          {msg}
        </Secondary>
      </div>
    </Overlay>
  );
};

const isCompleted = (wsRefId: string, viewRefId?: string) => {
  if (window.regressionInfo == null) return false;
  if (window.regressionInfo.completedWorkspaceRefIds?.includes(wsRefId))
    return true;
  if (
    !!viewRefId &&
    window.regressionInfo.completedViewRefIds?.includes(viewRefId)
  )
    return true;
  return false;
};

const runRegressionTest = async (beginWorkspaceRefId: string) => {
  const viewSleepTime = 3000;
  const mp = document;

  const workspaceDropDown: any = mp.getElementsByClassName('workspace-name')[0];
  workspaceDropDown.click();
  await sleep(1000);

  let workspaces: any = mp.getElementsByClassName('ws-list-item');
  let fromWSRefId = beginWorkspaceRefId;

  for (const ws of workspaces) {
    const wsRefId = ws.getAttribute('data-id');
    if (!!fromWSRefId) {
      if (wsRefId == fromWSRefId) fromWSRefId = null;
      else continue;
    }
    if (isCompleted(wsRefId)) continue;
    ws.click();
    await sleep(10000);
    //if (isCompleted(wsRefId)) continue;

    const folders: any = mp.getElementsByClassName('folder-list-item');
    for (const folder of folders) {
      const arrows = folder.parentElement.getElementsByClassName(
        'cpp-icon-arrow-right'
      );
      if (arrows.length) {
        folder.click();
        await sleep(300);
      }
    }

    let views: any = mp.getElementsByClassName('view-list-item');
    for (const view of views) {
      const viewRefId = view.getAttribute('data-id');
      if (isCompleted(wsRefId, viewRefId)) continue;
      view.scrollIntoView(false);
      view.click();
      const loaders = document.querySelectorAll(
        'div.cpp-ui.cpp-loader-overlay:not(#regressionOverlay)'
      );
      console.log('loaders.length', loaders.length);
      if (loaders.length) {
        console.log('waiting..');
        const s = new Date().getTime();
        await onElementRemoved(loaders[0]);
        await sleep(500);
        console.log('removed...', new Date().getTime() - s);
      } else {
        await sleep(viewSleepTime);
      }
    }
    await sleep(3000);
    workspaceDropDown.click();
    await sleep(300);
  }
};

const onElementRemoved = element =>
  new Promise(resolve => {
    new MutationObserver(function (mutations) {
      if (!document.body.contains(element)) {
        resolve(true);
        this.disconnect();
      }
    }).observe(element.parentElement, { childList: true });
  });
